// This device's key pair, kept in IndexedDB (M28, D55). The private key is a
// non-extractable CryptoKey: IndexedDB can hold it, but nothing can read its
// bytes, not even this app.

export interface StoredDevice {
  /** The server's id for this device's public key. */
  id: string;
  privateKey: CryptoKey;
  publicKey: string;
}

/**
 * Whether this browser lets the key store work (D262). `blocked`: a setting
 * keeps IndexedDB from this site — it is missing, or `open` is refused
 * (`SecurityError`, or Chromium's `UnknownError` "The user denied permission
 * to access the database" behind Brave Shields and "Block all cookies").
 * `broken`: IndexedDB is allowed but doesn't work — Chromium's `UnknownError`
 * "Internal error opening backing store" from a damaged or locked profile
 * database (the owner's Brave, 2026-10-26), a hang, or a write that doesn't
 * read back; only clearing the site's data mends it. `slow`: `open` didn't
 * answer within the bound — an old phone or a cold start under load; a
 * retry, never the clear-site-data advice. `ephemeral`: it works, but what
 * the previous visit wrote is gone while the session in localStorage
 * survived ("Forget me when I close this site" and friends).
 */
export type KeyStoreState = 'ok' | 'blocked' | 'broken' | 'slow' | 'ephemeral';

export interface KeyStoreProbe {
  state: KeyStoreState;
  /** The failure's `name` (nothing else: an M67 report carries it, D254). */
  error?: string;
}

const errorName = (err: unknown) =>
  err !== null &&
  typeof err === 'object' &&
  typeof (err as Error).name === 'string'
    ? (err as Error).name
    : 'Error';

/** A refusal by a setting, as against a store that is allowed but fails. */
function refused(err: unknown): boolean {
  if (errorName(err) === 'SecurityError') return true;
  const message =
    err !== null &&
    typeof err === 'object' &&
    typeof (err as Error).message === 'string'
      ? (err as Error).message
      : '';
  return /denied|permission|not allowed/i.test(message);
}

const DB_PREFIX = 'twinden-keys-';
const STORE = 'device';
// A mark written once, so the next visit can tell the store was emptied.
const INSTALLED_KEY = 'installed';
const installedMarker = (userId: string) => `twinden:keyStore:${userId}`;
// Old device records are kept under `previous:<id>` when a fresh key replaces
// them (D262), so a wrong call can be undone; the server row goes once the
// new key has received a share, or after a week.
const PREVIOUS_PREFIX = 'previous:';
// Chromium has been seen never answering `open` on a damaged backing store;
// a hang here would keep every API response waiting on `ready` for good.
export const OPEN_TIMEOUT_MS = 15_000;

function openRequest(
  name: string,
  version: number | undefined
): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB is not available'));
      return;
    }
    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      reject(
        new DOMException('IndexedDB did not open in time', 'TimeoutError')
      );
    }, OPEN_TIMEOUT_MS);
    // A synchronous throw (SecurityError in some browsers) rejects too.
    const request =
      version === undefined
        ? indexedDB.open(name)
        : indexedDB.open(name, version);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE)) {
        request.result.createObjectStore(STORE);
      }
    };
    request.onsuccess = () => {
      clearTimeout(timer);
      const db = request.result;
      // Another tab is upgrading: let it, rather than block it.
      db.onversionchange = () => db.close();
      // Answered after the bound: nobody is waiting, so don't hold the
      // connection open against a later version change.
      if (timedOut) db.close();
      else resolve(db);
    };
    request.onerror = () => {
      clearTimeout(timer);
      reject(request.error ?? new Error('IndexedDB could not open'));
    };
    // `blocked`: another tab holds an older version open. It usually clears
    // in a moment (every connection here closes after its transaction); the
    // timer above bounds the wait.
  });
}

/**
 * Opens this user's key database at whatever version it has (D262). The
 * store used to be opened at a fixed version 1, which fails with
 * `VersionError` if the database is ever newer; opening without a version
 * and upgrading only when the store is missing can't.
 */
async function open(userId: string): Promise<IDBDatabase> {
  const name = DB_PREFIX + userId;
  const db = await openRequest(name, undefined);
  if (db.objectStoreNames.contains(STORE)) return db;
  const next = db.version + 1;
  db.close();
  return openRequest(name, next);
}

function run<T>(
  db: IDBDatabase,
  mode: IDBTransactionMode,
  action: (store: IDBObjectStore) => IDBRequest<T>
) {
  return new Promise<T>((resolve, reject) => {
    try {
      const tx = db.transaction(STORE, mode);
      const request = action(tx.objectStore(STORE));
      tx.oncomplete = () => resolve(request.result);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error ?? new Error('Transaction aborted'));
    } catch (err) {
      reject(err);
    }
  });
}

function readMarker(userId: string): boolean {
  try {
    return localStorage.getItem(installedMarker(userId)) !== null;
  } catch {
    return false;
  }
}

function writeMarker(userId: string) {
  try {
    localStorage.setItem(installedMarker(userId), String(Date.now()));
  } catch {
    // No localStorage: ephemeral storage just can't be told apart.
  }
}

function clearMarker(userId: string) {
  try {
    localStorage.removeItem(installedMarker(userId));
  } catch {
    // Nothing to clear.
  }
}

/**
 * Can this browser keep a device key (D262)? Opens the store, writes a mark
 * and reads it back. Called before anything registers a key, so a browser
 * that blocks site data never piles up device keys on the server.
 */
export async function probeKeyStore(userId: string): Promise<KeyStoreProbe> {
  if (typeof indexedDB === 'undefined') {
    return { state: 'blocked', error: 'Unavailable' };
  }
  let db: IDBDatabase;
  try {
    db = await open(userId);
  } catch (err) {
    if (errorName(err) === 'TimeoutError') {
      return { state: 'slow', error: 'TimeoutError' };
    }
    return {
      state: refused(err) ? 'blocked' : 'broken',
      error: errorName(err),
    };
  }
  try {
    const seen = await run(db, 'readonly', (store) => store.get(INSTALLED_KEY));
    const stamp = Date.now();
    await run(db, 'readwrite', (store) => store.put(stamp, INSTALLED_KEY));
    const back = await run(db, 'readonly', (store) => store.get(INSTALLED_KEY));
    if (back !== stamp) return { state: 'broken', error: 'ReadBackMismatch' };
    const remembered = readMarker(userId);
    writeMarker(userId);
    return { state: seen === undefined && remembered ? 'ephemeral' : 'ok' };
  } catch (err) {
    return {
      state: refused(err) ? 'blocked' : 'broken',
      error: errorName(err),
    };
  } finally {
    db.close();
  }
}

export async function loadDevice(userId: string): Promise<StoredDevice | null> {
  if (typeof indexedDB === 'undefined') return null;
  const db = await open(userId);
  try {
    return (
      ((await run(db, 'readonly', (store) => store.get('self'))) as
        | StoredDevice
        | undefined) ?? null
    );
  } finally {
    db.close();
  }
}

export async function saveDevice(
  userId: string,
  device: StoredDevice,
  /** M78: its ML-KEM key, kept in the same transaction (D336). */
  mlkem?: StoredMlkem
) {
  const db = await open(userId);
  try {
    await run(db, 'readwrite', (store) => {
      if (mlkem) store.put(mlkem, MLKEM_KEY);
      return store.put(device, 'self');
    });
  } finally {
    db.close();
  }
}

/** A device record set aside when a fresh key replaced it (D262). */
export interface PreviousDevice extends StoredDevice {
  /** When it was replaced; its server row goes after a week at the latest. */
  replacedAt: number;
  /** It failed against the server's key too, so it opened nothing. */
  verified?: boolean;
  /** Set aside because the server no longer listed it (D55): never deleted. */
  lost?: boolean;
  /** Its private key opens the server's key for its id: never deleted. */
  keep?: boolean;
  /**
   * Replaced because its ML-KEM key was lost (M78, D336): its row goes at
   * once, whatever its P-256 key still opens (final review, point 1).
   */
  pqLost?: boolean;
}

// Each set-aside record has its own key (`previous:<id>`), so a second
// replacement never overwrites a first that is still waiting to be retired.
const previousKey = (id: string) => `${PREVIOUS_PREFIX}${id}`;
const PREVIOUS_RANGE = () =>
  IDBKeyRange.bound(PREVIOUS_PREFIX, `${PREVIOUS_PREFIX}\uffff`);

/** Keeps the record a fresh key is replacing, so the swap can be undone. */
export async function savePreviousDevice(
  userId: string,
  device: PreviousDevice
) {
  const db = await open(userId);
  try {
    await run(db, 'readwrite', (store) =>
      store.put(device, previousKey(device.id))
    );
    const back = await run(db, 'readonly', (store) =>
      store.get(previousKey(device.id))
    );
    if (!back) throw new DOMException('Not kept', 'ReadBackMismatch');
  } finally {
    db.close();
  }
}

export async function loadPreviousDevices(
  userId: string
): Promise<PreviousDevice[]> {
  if (typeof indexedDB === 'undefined') return [];
  const db = await open(userId);
  try {
    return (await run(db, 'readonly', (store) =>
      store.getAll(PREVIOUS_RANGE())
    )) as PreviousDevice[];
  } finally {
    db.close();
  }
}

export async function deletePreviousDevice(userId: string, id: string) {
  if (typeof indexedDB === 'undefined') return;
  const db = await open(userId);
  try {
    await run(db, 'readwrite', (store) => store.delete(previousKey(id)));
  } finally {
    db.close();
  }
}

/** Logging out forgets this device's key for good (D55). */
export function deleteDeviceStore(userId: string) {
  clearMarker(userId);
  if (typeof indexedDB === 'undefined') return;
  try {
    indexedDB.deleteDatabase(DB_PREFIX + userId);
  } catch {
    // Blocked storage has nothing to delete.
  }
}

/**
 * The last key information fetched for a chat or family (M29, D67): wrapped
 * keys and members' devices, nothing secret, so this device can still seal
 * a message without a connection.
 */
export async function saveScopeInfo(
  userId: string,
  scope: string,
  info: unknown
) {
  if (typeof indexedDB === 'undefined') return;
  const db = await open(userId);
  try {
    await run(db, 'readwrite', (store) => store.put(info, `scope:${scope}`));
  } finally {
    db.close();
  }
}

export async function loadScopeInfo<T>(
  userId: string,
  scope: string
): Promise<T | null> {
  if (typeof indexedDB === 'undefined') return null;
  const db = await open(userId);
  try {
    return (
      ((await run(db, 'readonly', (store) => store.get(`scope:${scope}`))) as
        | T
        | undefined) ?? null
    );
  } finally {
    db.close();
  }
}

/** A family was deleted (M54, D191): its saved key details go too. */
export async function deleteScopeInfo(userId: string, scope: string) {
  if (typeof indexedDB === 'undefined') return;
  const db = await open(userId);
  try {
    await run(db, 'readwrite', (store) => store.delete(`scope:${scope}`));
  } finally {
    db.close();
  }
}

// ---- My record of devices (M73, D300) --------------------------------------
// Kept in this user's key database, so signing out (deleteDeviceStore)
// forgets it with the device key.

const TRUST_KEY = 'trust';

/** This device's record of everyone's devices, or null before the first run. */
export async function loadTrustRecord<T>(userId: string): Promise<T | null> {
  if (typeof indexedDB === 'undefined') return null;
  const db = await open(userId);
  try {
    return (
      ((await run(db, 'readonly', (store) => store.get(TRUST_KEY))) as
        | T
        | undefined) ?? null
    );
  } finally {
    db.close();
  }
}

/**
 * Reads, changes and writes the record in one transaction (D300), so two
 * tabs learning the same device can't lose each other's update, and only
 * one of them sees it as new. `change` must be synchronous: an IndexedDB
 * transaction ends at the first await.
 */
export async function updateTrustRecord<T>(
  userId: string,
  change: (current: T | null) => T
): Promise<T> {
  if (typeof indexedDB === 'undefined') return change(null);
  const db = await open(userId);
  try {
    return await new Promise<T>((resolve, reject) => {
      let next: T | undefined;
      try {
        const tx = db.transaction(STORE, 'readwrite');
        const store = tx.objectStore(STORE);
        const read = store.get(TRUST_KEY);
        read.onsuccess = () => {
          try {
            next = change((read.result as T | undefined) ?? null);
            store.put(next, TRUST_KEY);
          } catch (err) {
            tx.abort();
            reject(err);
          }
        };
        tx.oncomplete = () => resolve(next as T);
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(tx.error ?? new Error('Transaction aborted'));
      } catch (err) {
        reject(err);
      }
    });
  } finally {
    db.close();
  }
}

// ---- v1 ceilings (M76, D328) -----------------------------------------------
// Per chat or family, what the v1 ceiling is worked out from: the ceiling
// from the last fresh keys answer, where v2 began, and the lowest epoch this
// device opened v2 under. Kept with the device key (signing out forgets it).

const ceilingKey = (scope: string) => `v1ceiling:${scope}`;

export interface KeptCeiling {
  kept?: number | null;
  v2Since?: number | null;
  lowestV2?: number | null;
}

function asKept(value: unknown): KeptCeiling | null {
  // A bare number is an earlier build's ceiling.
  if (typeof value === 'number') return { kept: value };
  return value && typeof value === 'object' ? (value as KeptCeiling) : null;
}

/** The scope's kept ceiling inputs, or null when none are written yet. */
export async function loadCeilingState(
  userId: string,
  scope: string
): Promise<KeptCeiling | null> {
  if (typeof indexedDB === 'undefined') return null;
  const db = await open(userId);
  try {
    return asKept(
      await run(db, 'readonly', (store) => store.get(ceilingKey(scope)))
    );
  } finally {
    db.close();
  }
}

/**
 * Changes the scope's kept inputs in one transaction (`change` gets the
 * stored ones, or null), so two tabs can't undo each other.
 */
export async function updateCeilingState(
  userId: string,
  scope: string,
  change: (stored: KeptCeiling | null) => KeptCeiling
): Promise<KeptCeiling> {
  if (typeof indexedDB === 'undefined') return change(null);
  const db = await open(userId);
  try {
    return await new Promise<KeptCeiling>((resolve, reject) => {
      let value: KeptCeiling = {};
      try {
        const tx = db.transaction(STORE, 'readwrite');
        const store = tx.objectStore(STORE);
        const read = store.get(ceilingKey(scope));
        read.onsuccess = () => {
          value = change(asKept(read.result));
          store.put(value, ceilingKey(scope));
        };
        tx.oncomplete = () => resolve(value);
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(tx.error ?? new Error('Transaction aborted'));
      } catch (err) {
        reject(err);
      }
    });
  } finally {
    db.close();
  }
}

// ---- The ML-KEM key (M78, docs/post-quantum.md §4.1) -----------------------
// The 64-byte seed (d ‖ z), sealed with a non-extractable AES-256-GCM key
// that IndexedDB keeps beside it (scope `mlkem-key`): nothing reads the
// sealing key's bytes, so a copy of the database's values alone doesn't
// give the seed. Opened into memory for the page's life (§4.4). Signing out
// deletes the whole store, so it goes with the P-256 key (D55).

const MLKEM_KEY = 'mlkem';

export interface StoredMlkem {
  /** The device key this ML-KEM key belongs to (a new device, a new key). */
  deviceId: string;
  /** The published 1184-byte `ek`, base64. */
  publicKey: string;
  /** Non-extractable AES-256-GCM, `mlkem-key`. */
  sealKey: CryptoKey;
  iv: Uint8Array<ArrayBuffer>;
  sealed: ArrayBuffer;
  /** The server accepted it (registration or PUT). */
  published: boolean;
  /** Registered with it: every copy for this device is from after (§6.2). */
  fromRegistration: boolean;
}

export async function loadMlkem(userId: string): Promise<StoredMlkem | null> {
  if (typeof indexedDB === 'undefined') return null;
  const db = await open(userId);
  try {
    return (
      ((await run(db, 'readonly', (store) => store.get(MLKEM_KEY))) as
        | StoredMlkem
        | undefined) ?? null
    );
  } finally {
    db.close();
  }
}

export async function saveMlkem(userId: string, value: StoredMlkem) {
  if (typeof indexedDB === 'undefined') return;
  const db = await open(userId);
  try {
    await run(db, 'readwrite', (store) => store.put(value, MLKEM_KEY));
  } finally {
    db.close();
  }
}

// ---- PQ ceilings and the once-per-scope rotation (M78, D339) ----------------
// Per chat or family, beside M76's v1 ceilings under their own name: the
// highest epoch of the copies present at this device's first look after
// its ML-KEM key was accepted (v1 copies above it from a PQ wrapper are
// refused, §6.2), and whether the backstop rotation ran (§6.3).

const pqKey = (scope: string) => `pqceiling:${scope}`;

export interface KeptPq {
  ceiling?: number;
  rotated?: boolean;
}

export async function loadPqState(
  userId: string,
  scope: string
): Promise<KeptPq | null> {
  if (typeof indexedDB === 'undefined') return null;
  const db = await open(userId);
  try {
    return (
      ((await run(db, 'readonly', (store) => store.get(pqKey(scope)))) as
        | KeptPq
        | undefined) ?? null
    );
  } finally {
    db.close();
  }
}

/** Changes a scope's kept PQ state in one transaction (tabs don't undo). */
export async function updatePqState(
  userId: string,
  scope: string,
  change: (stored: KeptPq | null) => KeptPq
): Promise<KeptPq> {
  if (typeof indexedDB === 'undefined') return change(null);
  const db = await open(userId);
  try {
    return await new Promise<KeptPq>((resolve, reject) => {
      let value: KeptPq = {};
      try {
        const tx = db.transaction(STORE, 'readwrite');
        const store = tx.objectStore(STORE);
        const read = store.get(pqKey(scope));
        read.onsuccess = () => {
          value = change((read.result as KeptPq | undefined) ?? null);
          store.put(value, pqKey(scope));
        };
        tx.oncomplete = () => resolve(value);
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(tx.error ?? new Error('Transaction aborted'));
      } catch (err) {
        reject(err);
      }
    });
  } finally {
    db.close();
  }
}
